import React from 'react'
import { Chip } from '@mui/material'
import type { ChipProps, SxProps, Theme } from '@mui/material'

export type FPStatus = 'Onboarding' | 'Active' | 'Terminated' | 'Test'

/** All FP statuses in display order. */
export const FP_STATUSES: FPStatus[] = ['Onboarding', 'Active', 'Terminated', 'Test']

/**
 * Canonical FP-status chip colouring, shared by the admin list's editable chip
 * (`EditableStatusChip`) and the Franchise Partner form. Active=green,
 * Onboarding=blue; Terminated/Test fall back to `default` + a custom sx below.
 */
export const fpStatusColor = (status: FPStatus): 'success' | 'info' | 'default' => {
  switch (status) {
    case 'Active':
      return 'success'
    case 'Onboarding':
      return 'info'
    case 'Terminated':
    case 'Test':
    default:
      return 'default'
  }
}

/** Chip sizing + the custom (non-palette) fills for Terminated / Test. */
export const fpStatusChipSx = (status: FPStatus): SxProps<Theme> => ({
  height: 22,
  ...(status === 'Terminated' && { bgcolor: 'grey.600', color: '#fff' }),
  ...(status === 'Test' && { bgcolor: '#9c27b0', color: '#fff' }),
})

export interface FpStatusChipProps extends Omit<ChipProps, 'color' | 'label'> {
  status: FPStatus
}

/**
 * Read-only FP status chip with the shared admin colours. For the inline-edit
 * variant used in the admin list, see `EditableStatusChip`.
 */
const FpStatusChip: React.FC<FpStatusChipProps> = ({ status, sx, ...props }) => (
  <Chip
    label={status}
    size="small"
    color={fpStatusColor(status)}
    variant="filled"
    sx={[fpStatusChipSx(status), ...(Array.isArray(sx) ? sx : [sx])] as SxProps<Theme>}
    {...props}
  />
)

export default FpStatusChip
